/**
 * Dimension helpers — feet/inches <-> decimal-feet conversion.
 *
 * The app stores every dimension as a decimal-feet `number` (RoomDimensions,
 * CeilingZone, WallConfig, etc.). These helpers let the DimensionInput render
 * that number as feet + inches without changing the stored format.
 *
 * Rules (agreed with product):
 *  - Inches range 0–11; 12 in carries to 1 ft.
 *  - ft/in -> decimal: feet + inches/12, rounded to 2 dp.
 *  - decimal -> ft/in: round to nearest whole inch; if that yields 12, carry
 *    to feet (result stays 0–11 inches).
 *  - All committed/stored values are rounded to 2 dp so the number stays clean
 *    (e.g. 10.67, never 10.66666667) and the ft/in<->decimal round-trip is stable.
 */

export type UnitMode = 'ftin' | 'decimal';

/** Round to 2 decimal places (the canonical stored precision). */
export const round2 = (n: number): number => Math.round(n * 100) / 100;

/** Decimal feet -> total whole inches (nearest inch). Makes carry exact. */
export const toTotalInches = (decimalFeet: number): number => Math.round(decimalFeet * 12);

/** Total whole inches -> { feet, inches } with inches in 0–11. */
export const fromTotalInches = (total: number): { feet: number; inches: number } => {
  const feet = Math.floor(total / 12);
  return { feet, inches: total - feet * 12 };
};

/** feet + inches -> decimal feet (2 dp). */
export const feetInchesToDecimal = (feet: number, inches: number): number =>
  round2(feet + inches / 12);

/** decimal feet -> { feet, inches } rounded to whole inches (carry handled). */
export const decimalToFeetInches = (decimalFeet: number): { feet: number; inches: number } =>
  fromTotalInches(toTotalInches(decimalFeet));

/** Clamp a number to [min, max]. */
export const clampValue = (v: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, v));

/** 10.5 -> 10' 6" (feet and inches, as the planner labels its dimensions). */
export const formatFeetInches = (feet: number): string => {
  const p = decimalToFeetInches(feet);
  return p.inches === 0 ? `${p.feet}'` : `${p.feet}' ${p.inches}"`;
};
